import { PERMANENT_COOKIE_NAME } from 'constants/cookies';
import {
  AMOUNT_FIELD,
  SENDER_COUNTRY_FIELD,
  SET_VALIDATION_ERRORS,
} from 'constants/fields';
import { HTTPStatuses, type HttpError } from 'constants/http';
import {
  FETCH_OFFERS_FAILURE,
  FETCH_OFFERS_REQUEST,
  FETCH_OFFERS_SUCCESS,
} from 'constants/offers';
import {
  FETCH_RECURRING_OFFERS_FAILURE,
  FETCH_RECURRING_OFFERS_REQUEST,
  FETCH_RECURRING_OFFERS_SUCCESS,
} from 'constants/recurringOffers';
import { schemas } from 'constants/schemas';
import { normalize } from 'normalizr';
import {
  getCurrentCountryCode,
  getCurrentLocale,
  getFieldValue,
  getOrderTemplate,
  getRecipientId,
} from 'selectors';
import { getRecurringConfigurationId, isFetching } from 'selectors/ui/ui';
import {
  VALIDATION_MESSAGES,
  validateAmount,
} from 'services/validation/validation';
import type { PayexThunkAction } from 'store/configureStore';
import { cookies } from 'utils/cookies/cookies';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';
import type { RootState } from '../../../../reducers/types';
import { entitiesPayload as payexEntitiesPayload } from '../../orders';

const buildRecipientFields = (state: RootState) => {
  const orderTemplate = getOrderTemplate(state);
  if (orderTemplate?.id) {
    return orderTemplate.recipient?.fields || [];
  }
  const recipientEntity = payexEntitiesPayload('recipient', state)?.recipient;
  return recipientEntity?.fields || [];
};

export const fetchOffers =
  (shouldShowCountrySelector = false): PayexThunkAction<void> =>
  async (dispatch, getState, { payex }) => {
    dispatch({ type: FETCH_OFFERS_REQUEST });
    const state = getState();

    const recipientId = getRecipientId(state);
    const amount = getFieldValue(state, AMOUNT_FIELD);
    const country = getCurrentCountryCode(state);
    const locale = getCurrentLocale(state);
    const fingerprint = cookies.read(PERMANENT_COOKIE_NAME) || 'no_fingerprint';
    const recurringConfigurationId = getRecurringConfigurationId(state);

    const recipientFields = buildRecipientFields(state);

    const validationErrors = {};

    const amountValidationErrors = validateAmount(amount);
    if (amountValidationErrors.length > 0) {
      validationErrors[AMOUNT_FIELD] = amountValidationErrors;
    }

    if (!country && !shouldShowCountrySelector) {
      validationErrors[SENDER_COUNTRY_FIELD] = [VALIDATION_MESSAGES.IS_MISSING];
    }

    if (Object.keys(validationErrors).length > 0) {
      dispatchValidationError(dispatch, validationErrors);
      return;
    }

    try {
      const response = await payex.offers.getAll({
        amount,
        country,
        fingerprint,
        locale,
        recipientId,
        recurringConfigurationId,
        recipientFields,
      });

      const singleOffersPayload = normalize(
        response.single,
        schemas.arrayOfOffers,
      );

      await dispatch({
        type: FETCH_OFFERS_SUCCESS,
        payload: singleOffersPayload,
      });

      const recurringOffersPayload = normalize(
        response?.recurring?.data || {},
        schemas.arrayOfOffers,
      );

      dispatch({
        type: FETCH_RECURRING_OFFERS_SUCCESS,
        payload: recurringOffersPayload,
        metadata: response?.recurring?.metadata || {},
      });
    } catch (error) {
      const errors = (error as HttpError)?.parsedBody?.errors || [];
      if (errors.length === 0) {
        return;
      }

      const payload = errors.reduce((acc, err) => {
        acc[err.param] = [err.message];
        return acc;
      }, {});

      dispatchValidationError(dispatch, payload);
    }
  };

export const fetchRecurringOffers =
  ({ installments }: { installments: number }): PayexThunkAction<void> =>
  (dispatch, getState, { payex }) => {
    const state = getState();

    const recipientId = getRecipientId(state);
    const country = getCurrentCountryCode(state);
    const amount = getFieldValue(state, AMOUNT_FIELD);
    const recurringConfigurationId = getRecurringConfigurationId(state);
    const recipientFields = buildRecipientFields(state);
    const locale = getCurrentLocale(state);
    const fingerprint = cookies.read(PERMANENT_COOKIE_NAME) || 'no_fingerprint';

    const isFetchingOffers = isFetching(state, 'recurringOffers');
    if (isFetchingOffers) {
      return Promise.resolve();
    }

    dispatch({
      type: FETCH_RECURRING_OFFERS_REQUEST,
    });

    return payex.offers
      .getRecurringOffers({
        recipientId,
        country,
        amount,
        installments,
        locale,
        fingerprint,
        recurringConfigurationId,
        recipientFields,
      })
      .then(response => {
        const recurringOffersPayload = normalize(
          response.data || {},
          schemas.arrayOfOffers,
        );

        dispatch({
          type: FETCH_RECURRING_OFFERS_SUCCESS,
          payload: recurringOffersPayload,
          metadata: response.metadata || {},
        });
      })
      .catch(error => {
        dispatch({ type: FETCH_RECURRING_OFFERS_FAILURE });
        const { status } = error;

        if (status !== HTTPStatuses.NOT_FOUND) {
          error.message = `${FETCH_OFFERS_FAILURE} ${error.message}`;
          errorNotifier.notifyWithFingerprint(error, [
            FETCH_RECURRING_OFFERS_FAILURE,
            status,
          ]);
        }
      });
  };

const dispatchValidationError = (dispatch, payload) => {
  dispatch({ type: SET_VALIDATION_ERRORS, payload });
  dispatch({ type: FETCH_OFFERS_FAILURE });

  throw new Error();
};
